/**
 * 8D RC/CA scoring fields for the Corrective Action Report.
 *
 * The customer grades the supplier's 8D with two 1-5 scores when reviewing a
 * submitted CAR. Both are persisted on the NCR (`rc_score` / `ca_score`) and
 * are surfaced as columns in the Quality NCR list.
 *
 * Rendered in one of two modes:
 * - `editable` — the customer is reviewing a CAR at status `car_submitted`
 *   and may still change the grades.
 * - read-only — the review window has closed (NCR approved/closed) or the
 *   viewer is not the reviewing customer. The grades still render so the
 *   score stays auditable on the NCR itself; previously the whole scoring
 *   block was mounted only alongside the Approve / Reject buttons, so
 *   clicking Approve unmounted it and the grade vanished from the page
 *   even though it was saved.
 */

import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

/** 8D scoring dropdown options (1-5). Value stays the integer; the label
 *  spells out what each grade means so the customer scores consistently. */
export const RC_SCORE_OPTIONS = [
  { value: '1', label: '1 — Insufficient (symptom only, e.g. "human error")' },
  { value: '2', label: '2' },
  { value: '3', label: '3 — Basic (partial cause identified)' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — Excellent (proven root cause with data + validation)' },
];

export const CA_SCORE_OPTIONS = [
  { value: '1', label: '1 — Administrative (training, instruction)' },
  { value: '2', label: '2' },
  { value: '3', label: '3 — Process Improvement' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — System-level fix (error-proofing)' },
];

/** Resolve a stored integer score to its descriptive option label. */
function scoreLabel(
  score: number | null | undefined,
  options: { value: string; label: string }[]
): string | null {
  if (score == null) return null;
  return options.find((o) => o.value === String(score))?.label ?? String(score);
}

interface CarScoreFieldsProps {
  rcScore?: number | null;
  caScore?: number | null;
  /** When false the grades render as static text instead of dropdowns. */
  editable: boolean;
  onRcScoreChange?: (score: number) => void;
  onCaScoreChange?: (score: number) => void;
}

export function CarScoreFields({
  rcScore,
  caScore,
  editable,
  onRcScoreChange,
  onCaScoreChange,
}: CarScoreFieldsProps) {
  if (!editable) {
    const rcLabel = scoreLabel(rcScore, RC_SCORE_OPTIONS);
    const caLabel = scoreLabel(caScore, CA_SCORE_OPTIONS);
    return (
      <div className="flex flex-wrap gap-6">
        <div className="space-y-1.5">
          <Label>RC Score</Label>
          <p className="text-sm font-medium">{rcLabel ?? 'Not scored'}</p>
          <p className="text-xs text-muted-foreground">Rating for strength of Root Cause</p>
        </div>
        <div className="space-y-1.5">
          <Label>CA Score</Label>
          <p className="text-sm font-medium">{caLabel ?? 'Not scored'}</p>
          <p className="text-xs text-muted-foreground">Score for strength of Corrective Action</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-6 mb-4">
      <div className="space-y-1.5">
        <Label htmlFor="rc-score">RC Score</Label>
        {/* '' (not undefined) when unscored so the Select stays controlled
            from first render — Radix shows the placeholder for ''. */}
        <Select
          value={rcScore != null ? String(rcScore) : ''}
          onValueChange={(v) => onRcScoreChange?.(parseInt(v))}
        >
          <SelectTrigger id="rc-score" className="w-72 max-w-full">
            <SelectValue placeholder="Select 1–5" />
          </SelectTrigger>
          <SelectContent>
            {RC_SCORE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Rating for strength of Root Cause</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ca-score">CA Score</Label>
        <Select
          value={caScore != null ? String(caScore) : ''}
          onValueChange={(v) => onCaScoreChange?.(parseInt(v))}
        >
          <SelectTrigger id="ca-score" className="w-72 max-w-full">
            <SelectValue placeholder="Select 1–5" />
          </SelectTrigger>
          <SelectContent>
            {CA_SCORE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Score for strength of Corrective Action</p>
      </div>
    </div>
  );
}
